import type { ExportData } from '@/hooks/exportDropdown/types'

type SitemapOptions = {
  url: string
  lastmod: string
  changefreq: string
  priority: string
}

type SaveSitemapXMLParams = {
  tree: ExportData[]
  options: SitemapOptions
  usePageTitleAsLoc: boolean
}

export const useSitemapXMLExport = () => {
  const saveSitemapXml = ({
    tree,
    options,
    usePageTitleAsLoc = true,
  }: SaveSitemapXMLParams) => {
    const baseUrl = options.url || 'https://www.mywebsite.com'

    const doc = [
      '<?xml version="1.0" encoding="utf-8"?>',
      '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
      '<!--  created with sitemap generator https://octopus.do  -->',
      '',
    ]

    tree.forEach((t, index) => {
      const url = index === 0 ? [baseUrl] : [`${baseUrl}/${t.title}`]

      generate(t, url, doc, options, usePageTitleAsLoc)
    })

    doc.push('</urlset>')

    const blob = new Blob([doc.join('\n')], {
      type: 'application/xml;charset=utf-8',
    })
    const link = document.createElement('a')
    link.download = 'sitemap.xml'
    link.href = window.URL.createObjectURL(blob)
    link.click()
    window.URL.revokeObjectURL(link.href)
  }

  function generate(
    tree: ExportData,
    baseUrl: string[],
    doc: string[],
    options: SitemapOptions,
    usePageTitleAsLoc: boolean
  ) {
    const nodeUrl = tree.url

    const loc = usePageTitleAsLoc
      ? baseUrl
          .map((cur: string) => {
            const curPath = cur.replace(/\s+/g, '-')
            if (curPath === '-' || curPath === '') {
              return 'page'
            }

            return curPath
          })
          .map((cur: string) => cur.toLocaleLowerCase())
          .join('/')
      : nodeUrl

    if (usePageTitleAsLoc || nodeUrl !== '') {
      doc.push('    <url>')
      doc.push(`        <loc>${loc}</loc>`)

      if (options.lastmod) {
        doc.push(`        <lastmod>${options.lastmod}</lastmod>`)
      }

      if (options.changefreq) {
        doc.push(`        <changefreq>${options.changefreq}</changefreq>`)
      }

      if (options.priority) {
        doc.push(`        <priority>${options.priority}</priority>`)
      }

      doc.push('    </url>')
    }

    if (tree.children.length > 0) {
      tree.children.forEach((t) => {
        generate(t, baseUrl.concat(t.title), doc, options, usePageTitleAsLoc)
      })
    }
  }

  return {
    saveSitemapXml,
  }
}
